package game.events

import domain.cards.CardDefinition
import domain.levels.RoomObjective
import engine.model.events.GameEvent

/**
 * Gravitar-domain game events extending the engine's [GameEvent] interface.
 *
 * These complement [engine.model.events.DefaultGameEvents] with higher-level
 * concepts the engine library doesn't know about (cards, rooms, freeze, abyss,
 * currency).
 *
 * Published via the **global** [engine.model.events.EventOrchestrator.publish] —
 * the same static bus the engine itself uses. Subscribers filter by [gameId]:
 *
 * ```kotlin
 * EventOrchestrator.subscribe<GravitarEvents.RoomObjectiveMet> { ev ->
 *     if (ev.gameId != myId) return@subscribe
 *     showCardPick(ev.room)
 * }
 * ```
 */
object GravitarEvents {

    // ── Story / room ──────────────────────────────────────────────────────────

    /** Room objective satisfied. Published before the card-pick screen appears. */
    data class RoomObjectiveMet(
        val objective: RoomObjective,
        val room: Int,
        override val gameId: String,
    ) : GameEvent

    /** Player picked a card. Published after it is applied to RunState. */
    data class CardPicked(
        val card: CardDefinition,
        val room: Int,
        override val gameId: String,
    ) : GameEvent

    // ── Freeze board ──────────────────────────────────────────────────────────

    /** Player topped out in freeze mode. Board cleared, play continues. */
    data class FreezeBoardTopOut(
        val survivedRoom: Int,
        val linesWiped: Int,
        override val gameId: String,
    ) : GameEvent

    /**
     * Board wipe complete, engine ready.
     * Published by [game.GravitarEngine.clearBoard].
     */
    data class FreezeBoardResumed(
        override val gameId: String,
    ) : GameEvent

    // ── Garbage ───────────────────────────────────────────────────────────────

    /** Blocked garbage lines spawned by a card effect. */
    data class BlockedGarbageSpawned(
        val lines: Int,
        val holeColumn: Int,
        val columnShiftInterval: Int,
        override val gameId: String,
    ) : GameEvent

    /** Garbage hole column shifted after enough clears. */
    data class GarbageHoleShifted(
        val newColumn: Int,
        override val gameId: String,
    ) : GameEvent

    // ── Abyss ─────────────────────────────────────────────────────────────────

    /** Playing field reduced one row. Published by [game.GravitarEngine.reducePlayingField]. */
    data class AbyssFloorRisen(
        val newFloorRow: Int,
        override val gameId: String,
    ) : GameEvent

    /** Player reached an altitude checkpoint in abyss mode. */
    data class AbyssCheckpointReached(
        val altitude: Int,
        override val gameId: String,
    ) : GameEvent

    // ── Versus ────────────────────────────────────────────────────────────────

    /** Garbage attack queued against [targetGameId]. */
    data class VersusAttackQueued(
        val targetGameId: String,
        val lines: Int,
        override val gameId: String,
    ) : GameEvent

    /** A versus match ended. */
    data class VersusMatchOver(
        val winnerGameId: String,
        val loserGameId: String,
        override val gameId: String,
    ) : GameEvent

    // ── Currency ──────────────────────────────────────────────────────────────

    /**
     * Coins awarded for a qualifying clear.
     * Published by [domain.score.StoryCurrencyCalculator.onScoreUpdated].
     *
     * @param amount  Final coin amount (after [RunState.totalCoinMultiplier]).
     * @param reason  Display name of the move type, e.g. "GRAVITAR", "PULL TRIPLE".
     */
    data class CoinsAwarded(
        val amount: Int,
        val reason: String,
        override val gameId: String,
    ) : GameEvent
}
